'use client';

import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HomepageImagesGalleryProps {
  currentLanguage?: string;
}

const HomepageImagesGallery = ({ currentLanguage = 'en' }: HomepageImagesGalleryProps) => {
  const images = [
    {
      src: '/assets/images/image-1779450559730.png',
      alt: 'Kolam and Rangoli traditional Indian art pattern representing sustainability, biodiversity, and the circular economy principles at the heart of GenCarbon ESG',
      caption: 'Sustainability Rooted in Culture',
    },
    {
      src: '/assets/images/image-1779450587358.png',
      alt: 'Green Evolution infographic showing the progression from traditional carbon markets to AI-powered quantum carbon credit verification using CAQSG framework',
      caption: 'Green Evolution: CAQSG™ Framework',
    },
    {
      src: '/assets/images/image-1779450605775.png',
      alt: 'Community planting initiative with wind turbines in background, illustrating renewable energy integration with agroforestry and carbon sequestration projects',
      caption: 'Community Planting & Renewable Energy',
    },
    {
      src: '/assets/images/image-1779450641620.png',
      alt: 'Sustainable Data Centre Model diagram showing energy flow, carbon footprint reduction strategies, and ESG compliance framework for IT infrastructure',
      caption: 'Sustainable Data Centre Model',
    },
    {
      src: '/assets/images/image-1779450667194.png',
      alt: 'Data Center energy community diagram illustrating how data centres can participate in carbon credit markets and community energy sharing programmes',
      caption: 'Data Centre Energy Community',
    },
    {
      src: '/assets/images/image-1779451489609.png',
      alt: 'GenCarbon sustainability initiative visual showcasing AI-powered carbon credit generation and ESG solutions for enterprises and communities',
      caption: 'GenCarbon Sustainability Initiative',
    },
  ];

  const standardsImages = [
    {
      src: '/assets/images/image-1779450771729.png',
      alt: 'Sustainable Procurement ISO 20400 standard overview — internationally recognised framework for embedding sustainability into procurement processes, supply chains, and vendor empanelment for Net Zero compliance',
      caption: 'Sustainable Procurement — ISO 20400',
      badge: 'ISO Standard',
      badgeColor: 'bg-blue-600',
    },
    {
      src: '/assets/images/image-1779450822320.png',
      alt: 'Popular Sustainability Standards and Certifications 2025 — comprehensive overview of globally recognised ESG frameworks including GRI, BRSR, SBTi, CDP, TCFD, ISO 14001, and carbon credit standards used by GenCarbon',
      caption: 'Popular Sustainability Standards & Certifications 2025',
      badge: '2025 Reference',
      badgeColor: 'bg-emerald-600',
    },
  ];

  return (
    <section className="py-14 md:py-20 bg-gradient-to-br from-surface via-background to-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-semibold mb-4">
            <Icon name="PhotoIcon" size={16} />
            GenCarbon ESG Visual Insights
          </div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-primary mb-3">
            Sustainability in Action
          </h2>
          <p className="text-text-secondary text-base max-w-2xl mx-auto">
            From cultural roots to cutting-edge data centre ESG models — our work spans the full spectrum of sustainable innovation.
          </p>
        </div>

        {/* Bento grid layout */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
          {/* Large featured image */}
          <div className="col-span-2 md:col-span-2 row-span-1 group relative rounded-2xl overflow-hidden shadow-natural-lg aspect-[16/9]">
            <AppImage
              src={images[0].src}
              alt={images[0].alt}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
              <p className="text-white text-sm font-semibold">{images[0].caption}</p>
            </div>
          </div>

          {/* Tall right image */}
          <div className="col-span-1 row-span-2 group relative rounded-2xl overflow-hidden shadow-natural-lg">
            <AppImage
              src={images[1].src}
              alt={images[1].alt}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
              <p className="text-white text-sm font-semibold">{images[1].caption}</p>
            </div>
          </div>

          {/* Bottom row — 3 equal images */}
          {images.slice(2).map((img, i) => (
            <div key={i} className="col-span-1 group relative rounded-2xl overflow-hidden shadow-natural-lg aspect-square">
              <AppImage
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                <p className="text-white text-xs font-semibold">{img.caption}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Standards & Certifications Reference Images */}
        <div className="mt-4">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full text-emerald-700 text-sm font-semibold mb-3">
              <Icon name="ShieldCheckIcon" size={16} />
              Global Standards We Work With
            </div>
            <h3 className="font-heading font-bold text-xl md:text-2xl text-primary mb-2">
              Internationally Recognised Sustainability Frameworks
            </h3>
            <p className="text-text-secondary text-sm max-w-2xl mx-auto">
              GenCarbon / JAN Carbon solutions are aligned with globally proven standards — making your sustainability credentials credible, auditable, and accepted worldwide.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {standardsImages.map((img, i) => (
              <div key={i} className="group relative rounded-2xl overflow-hidden shadow-natural-lg border border-border bg-white">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <AppImage
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 p-2"
                  />
                  <div className={`absolute top-3 left-3 ${img.badgeColor} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                    {img.badge}
                  </div>
                </div>
                <div className="p-4 bg-gradient-to-r from-primary/5 to-secondary/5 border-t border-border">
                  <p className="text-sm font-semibold text-primary text-center">{img.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomepageImagesGallery;
